import { useState } from "react";
import { useForm } from "react-hook-form";
import Image from "next/image";

import Button from "../components/button";
import Hero from "../components/hero";
import Input from "../components/input";
import Textarea from "../components/textarea";

import styles from "./careers.module.scss";
import TeamMember from "../components/team-member";
import OurValues from "../components/our-values";
import { getTeamMembers } from "../lib/team-members";

import { getSEOPageProps } from "../lib/seo";
import SeoMeta from "../components/seo-meta";
import config from "../config";
import { activeCampaignSubmit } from "../lib/forms";

// Active Campaign field names.
const fieldMap = {
  details: "field[105]"
};

export default function CareersPage({ teamMembers, seo }) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const { register, formState, handleSubmit, watch } = useForm({
    mode: "onChange",
    // Default Active Campaign values for this
    // specific form.
    defaultValues: {
      u: "33",
      f: "33",
      s: "0",
      c: "0",
      m: "0",
      act: "sub",
      v: "2",
      or: "5771fecfd87840481f137f515d8fd19a"
    }
  });

  watch();

  const onSubmit = (data) => {
    activeCampaignSubmit(data, false)
      .then(() => setSubmitted(true))
      .catch((e) => setError(e));
  };

  const onError = (e) => console.log(e);

  const { errors } = formState;

  const getError = (field) => {
    const fieldNum = field.split("[")[1].slice(0, -1);

    if (errors.field && errors.field[fieldNum]) {
      return errors.field[fieldNum].message;
    }

    return null;
  };

  return (
    <>
      <SeoMeta {...seo} />
      <div className="container">
        <Hero
          title="Work at East Room"
          description="East Room was founded in 2014 with the vision to develop an environment that enhances the personal and professional well-being of our members and extended community through intentional design, authentic programming, diverse community, and exceptional hospitality."
        />

        <figure className={styles.FeaturedImage}>
          <Image
            src="/images/careers.jpg"
            alt="Work at East Room"
            width="1244"
            height="679"
            layout="responsive"
            objectFit="cover"
          />
        </figure>
      </div>

      <div className="container">
        <div className="flex flex-col justify-between items-start mt-10 mb-24 gap-10 px-4 md:px-0 md:gap-24 md:flex-row md:mb-24">
          <div className={styles.FormContent}>
            <h2 className={styles.FormTitle}>Get in touch with us</h2>

            <p className="font-book mt-10">
              Submit this form and one of our community managers will get in
              touch if we have any available oppurtunitues.
            </p>
          </div>
          <div className="flex-1">
            {submitted && (
              <p className={styles.ConfirmationMessage}>
                Thank you, we will be in touch shortly!
              </p>
            )}

            {!submitted && (
              <form onSubmit={handleSubmit(onSubmit, onError)}>
                <div className="flex flex-row justify-between items-center gap-2">
                  <Input
                    type="text"
                    placeholder="First name *"
                    {...register("firstname", {
                      required: "First name is required"
                    })}
                    errorText={errors.firstname && errors.firstname.message}
                    inline
                  />
                  <Input
                    type="text"
                    placeholder="Last name *"
                    {...register("lastname", {
                      required: "Last name is required"
                    })}
                    errorText={errors.lastname && errors.lastname.message}
                    inline
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    placeholder="Email address *"
                    {...register("email", { required: "Email is required" })}
                    errorText={errors.email && errors.email.message}
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    placeholder="Phone number *"
                    {...register("phone", {
                      required: "Phone number is required"
                    })}
                    errorText={errors.phone && errors.phone.message}
                  />
                </div>
                <div>
                  <Textarea
                    type="text"
                    placeholder="Message *"
                    rows="1"
                    {...register(fieldMap.details, {
                      required: "Message is required"
                    })}
                    errorText={getError(fieldMap.details)}
                  />
                </div>

                {error && <p>{error}</p>}

                <div className="text-center md:text-right mt-4">
                  <Button rounded outline small type="submit">
                    Get in Touch
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className={styles.Mission}>
        <div className="container">
          <h3>Mission</h3>

          <p>
            Our mission is to develop an environment where work and life
            intersect through intentional design, authentic programming, diverse
            community and exceptional hospitality.
          </p>
        </div>
      </div>

      <div className="container">
        <div className={styles.MeetOurTeam}>
          <h2>Meet our Team</h2>

          <div className={styles.TeamWrapper}>
            {teamMembers.map((teamMember) => (
              <TeamMember key={teamMember.slug} teamMember={teamMember} />
            ))}
          </div>
        </div>
      </div>

      <div className="container">
        <OurValues />
      </div>
    </>
  );
}

export async function getStaticProps() {
  try {
    const [teamMembers, seo] = await Promise.all([
      getTeamMembers(),
      getSEOPageProps({ resolvedUrl: "/careers" })
    ]);

    return {
      props: {
        teamMembers,
        seo
      },
      revalidate: config.ISR_TIMING
    };
  } catch (e) {
    console.log(e.message);
  }
}
